// ============================================
// SELECTORCLIENTEEXPEDIENTE.JS
// ============================================
// EXP-B.1: jerarquia visible solo GRUPO -> SUCURSAL. La razon social es
// info contable que el vendedor no conoce; se infiere automaticamente
// desde el campo razon_social_id de la sucursal seleccionada y se
// guarda en el state para que el backend la reciba.
//
// Patron UX: COMBO click-to-open. Cada combo es un input que muestra el
// valor seleccionado y, al hacer click, abre un panel desplegable con:
//   - Buscador interno
//   - Lista filtrada con checkmark del seleccionado
//   - Click fuera cierra
//
// Reglas:
//   - Toggle "Crear sin cliente vinculado" arriba (limpia los IDs).
//   - Sucursal deshabilitada hasta que haya grupo elegido.
//   - Si hay grupo, la sucursal es obligatoria (validado fuera por
//     el componente que use este selector).
//   - Persona de contacto opcional una vez hay sucursal.
//
// Endpoints (existentes):
//   GET /api/clientes/grupos
//   GET /api/clientes/sucursales?grupo_id=X    (incluye sucursales huerfanas)
//   POST /api/clientes/personas
//   POST /api/clientes/roles-contacto

import React, { useEffect, useState } from 'react';
import { API_BASE_CLI } from './helpersClientes';
import ModalNuevoClienteExpediente from './ModalNuevoClienteExpediente';
import { authHeaders } from '../utils/storage';

const API = API_BASE_CLI;

const COLOR_AZUL    = '#185FA5';
const COLOR_LIMA    = '#94b803';
const COLOR_OSCURO  = '#270f2b';
const COLOR_BORDE   = '#e5e7eb';
const COLOR_GRIS    = '#5e666e';
const COLOR_GRIS_CL = '#9aa0a6';

const FF = "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

function valorVacio() {
  return {
    cliente_id: null,
    cliente_grupo_id: null,
    cliente_razon_social_id: null,
    cliente_sucursal_id: null,
    cliente_persona_id: null,
    _resumen: '',
    _detalle: null,
    _sinCliente: false,
  };
}

function construirResumen(detalle) {
  if (!detalle) return '';
  const partes = [];
  if (detalle.grupo?.nombre) partes.push(detalle.grupo.nombre);
  if (detalle.razon_social?.razon_social) partes.push(detalle.razon_social.razon_social);
  if (detalle.sucursal?.nombre_interno) partes.push(detalle.sucursal.nombre_interno);
  if (detalle.persona?.nombre_completo) partes.push(detalle.persona.nombre_completo);
  return partes.join(' > ');
}

export default function SelectorClienteExpediente({ valor, onChange, esCelular }) {
  const v = valor || valorVacio();

  const tieneIDs = !!(
    v.cliente_grupo_id || v.cliente_razon_social_id ||
    v.cliente_sucursal_id || v.cliente_persona_id
  );
  const [sinCliente, setSinCliente] = useState(!!v._sinCliente);
  useEffect(() => {
    if (tieneIDs && sinCliente) setSinCliente(false);
  }, [tieneIDs, sinCliente]);

  // EXP-CLIENTE-CUALQUIERA: tipo de vinculo. Se infiere de los IDs presentes
  // (al reabrir un valor ya cargado) y por defecto es 'grupo'.
  const tipoInicial =
    (v.cliente_persona_id && !v.cliente_grupo_id) ? 'persona' :
    (v.cliente_razon_social_id && !v.cliente_grupo_id && !v.cliente_sucursal_id) ? 'empresa' :
    'grupo';
  const [tipo, setTipo] = useState(tipoInicial);

  // Catalogos cargados desde el backend
  const [grupos, setGrupos]           = useState([]);
  const [cargandoGrupos, setCargandoGrupos] = useState(false);
  const [sucursales, setSucursales]   = useState([]); // del grupo actual (incluye huerfanas)
  const [cargandoSucursales, setCargandoSucursales] = useState(false);

  // EXP-CLIENTE-CUALQUIERA: catalogos de empresa (razon social) y persona.
  const [razones, setRazones]         = useState([]);
  const [cargandoRazones, setCargandoRazones] = useState(false);
  const [personas, setPersonas]       = useState([]);
  const [cargandoPersonas, setCargandoPersonas] = useState(false);

  const [modalNuevo, setModalNuevo]   = useState(false);
  const [agregarPersonaForm, setAgregarPersonaForm] = useState(null);
  const [guardandoPersona, setGuardandoPersona] = useState(false);

  // ===== CARGA INICIAL DE GRUPOS =====
  useEffect(() => {
    let activo = true;
    setCargandoGrupos(true);
    fetch(`${API}/api/clientes/grupos`, { headers: authHeaders() })
      .then(r => r.json())
      .then(r => {
        if (!activo) return;
        const list = Array.isArray(r?.grupos) ? r.grupos : [];
        setGrupos(list.filter(g => g.estado !== 'archivado'));
      })
      .catch(() => activo && setGrupos([]))
      .finally(() => activo && setCargandoGrupos(false));
    return () => { activo = false; };
  }, []);

  // ===== SUCURSALES DEL GRUPO ACTUAL =====
  // Incluye huerfanas (ancladas directo al grupo sin razon social).
  useEffect(() => {
    if (!v.cliente_grupo_id) {
      setSucursales([]);
      return;
    }
    let activo = true;
    setCargandoSucursales(true);
    fetch(`${API}/api/clientes/sucursales?grupo_id=${v.cliente_grupo_id}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(r => {
        if (!activo) return;
        const list = Array.isArray(r?.sucursales) ? r.sucursales : [];
        setSucursales(list.filter(x => x.estado !== 'archivado'));
      })
      .catch(() => activo && setSucursales([]))
      .finally(() => activo && setCargandoSucursales(false));
    return () => { activo = false; };
  }, [v.cliente_grupo_id]);

  // ===== RAZONES SOCIALES (empresas) — carga al activar la pestana =====
  useEffect(() => {
    if (tipo !== 'empresa' || razones.length > 0) return;
    let activo = true;
    setCargandoRazones(true);
    fetch(`${API}/api/clientes/razones-sociales`, { headers: authHeaders() })
      .then(r => r.json())
      .then(r => {
        if (!activo) return;
        const list = Array.isArray(r?.razones_sociales) ? r.razones_sociales : [];
        setRazones(list.filter(x => x.estado !== 'archivado'));
      })
      .catch(() => activo && setRazones([]))
      .finally(() => activo && setCargandoRazones(false));
    return () => { activo = false; };
  }, [tipo, razones.length]);

  // ===== PERSONAS — carga al activar la pestana =====
  useEffect(() => {
    if (tipo !== 'persona' || personas.length > 0) return;
    let activo = true;
    setCargandoPersonas(true);
    fetch(`${API}/api/clientes/personas`, { headers: authHeaders() })
      .then(r => r.json())
      .then(r => {
        if (!activo) return;
        const list = Array.isArray(r?.personas) ? r.personas : [];
        setPersonas(list.filter(x => x.estado !== 'archivado'));
      })
      .catch(() => activo && setPersonas([]))
      .finally(() => activo && setCargandoPersonas(false));
    return () => { activo = false; };
  }, [tipo, personas.length]);

  // ===== HANDLERS DE SELECCION =====
  const elegirGrupo = (grupo) => {
    const detalle = { grupo, razon_social: null, sucursal: null, persona: null };
    onChange({
      cliente_id: null,
      cliente_grupo_id: grupo.id,
      cliente_razon_social_id: null,
      cliente_sucursal_id: null,
      cliente_persona_id: null,
      _resumen: construirResumen(detalle),
      _detalle: detalle,
    });
  };

  // EXP-B.1: al elegir sucursal, inferimos automaticamente la razon social
  // a la que pertenece. La sucursal "huerfana" no tiene razon_social_id —
  // en ese caso la razon queda null (correcto: cuelga directo del grupo).
  const elegirSucursal = (sucursal) => {
    const razonInferida = sucursal.razon_social_id
      ? {
          id: sucursal.razon_social_id,
          razon_social: sucursal.razon_social_nombre || '',
        }
      : null;
    const detalle = {
      ...(v._detalle || {}),
      razon_social: razonInferida,
      sucursal,
    };
    onChange({
      ...v,
      cliente_razon_social_id: sucursal.razon_social_id || null,
      cliente_sucursal_id: sucursal.id,
      _resumen: construirResumen(detalle),
      _detalle: detalle,
    });
  };

  // EXP-CLIENTE-CUALQUIERA: vincular por EMPRESA (razon social). Solo setea
  // cliente_razon_social_id; las demas columnas quedan en null.
  const elegirRazon = (rs) => {
    const detalle = {
      grupo: null,
      razon_social: { id: rs.id, razon_social: rs.razon_social },
      sucursal: null, persona: null,
    };
    onChange({
      cliente_id: null,
      cliente_grupo_id: null,
      cliente_razon_social_id: rs.id,
      cliente_sucursal_id: null,
      cliente_persona_id: null,
      _resumen: construirResumen(detalle),
      _detalle: detalle,
    });
  };

  // EXP-CLIENTE-CUALQUIERA: vincular por PERSONA FISICA. Solo setea
  // cliente_persona_id; las demas columnas quedan en null.
  const elegirPersona = (p) => {
    const detalle = { grupo: null, razon_social: null, sucursal: null, persona: p };
    onChange({
      cliente_id: null,
      cliente_grupo_id: null,
      cliente_razon_social_id: null,
      cliente_sucursal_id: null,
      cliente_persona_id: p.id,
      _resumen: construirResumen(detalle),
      _detalle: detalle,
    });
  };

  const limpiarTodo = () => {
    onChange(valorVacio());
    setAgregarPersonaForm(null);
  };

  // Cambia el tipo de vinculo y limpia la seleccion previa (cada tipo usa su
  // propia columna; no mezclamos tipos en un mismo expediente).
  const cambiarTipo = (nuevo) => {
    if (nuevo === tipo) return;
    setTipo(nuevo);
    setAgregarPersonaForm(null);
    onChange(valorVacio());
  };

  const onCreado = (resultado) => {
    setModalNuevo(false);
    setSinCliente(false);
    // Ajusta la pestana segun el tipo de cliente recien creado.
    if (resultado.cliente_persona_id && !resultado.cliente_grupo_id) setTipo('persona');
    else if (resultado.cliente_razon_social_id && !resultado.cliente_grupo_id && !resultado.cliente_sucursal_id) setTipo('empresa');
    else setTipo('grupo');
    onChange(resultado);
  };

  const toggleSinCliente = (siguiente) => {
    setSinCliente(siguiente);
    setAgregarPersonaForm(null);
    // Propagamos la bandera al padre para que la validacion del submit sepa
    // que el "sin cliente" fue una eleccion explicita.
    onChange(siguiente ? { ...valorVacio(), _sinCliente: true } : valorVacio());
  };

  // ===== PERSONA INLINE =====
  const guardarPersonaInline = async () => {
    if (!agregarPersonaForm) return;
    const nombre = agregarPersonaForm.nombre.trim();
    if (!nombre) return;
    setGuardandoPersona(true);
    try {
      const r = await fetch(`${API}/api/clientes/personas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          nombre_completo: nombre,
          celular: agregarPersonaForm.celular || null,
          email: agregarPersonaForm.email || null,
        }),
      }).then(x => x.json());
      if (!r.ok || !r.persona) throw new Error(r.error || 'No se pudo crear persona');

      let alcanceTipo = null, alcanceId = null;
      if (v.cliente_sucursal_id) { alcanceTipo = 'sucursal'; alcanceId = v.cliente_sucursal_id; }
      else if (v.cliente_razon_social_id) { alcanceTipo = 'razon_social'; alcanceId = v.cliente_razon_social_id; }
      else if (v.cliente_grupo_id) { alcanceTipo = 'grupo'; alcanceId = v.cliente_grupo_id; }
      if (alcanceTipo) {
        try {
          await fetch(`${API}/api/clientes/roles-contacto`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify({
              persona_id: r.persona.id,
              tipo: 'contacto',
              alcance_tipo: alcanceTipo,
              alcance_id: alcanceId,
            }),
          });
        } catch { /* best-effort */ }
      }
      const nuevoDetalle = { ...(v._detalle || {}), persona: r.persona };
      onChange({
        ...v,
        cliente_persona_id: r.persona.id,
        _detalle: nuevoDetalle,
        _resumen: construirResumen(nuevoDetalle),
      });
      setAgregarPersonaForm(null);
    } catch (e) {
      alert('Error: ' + (e.message || 'no se pudo guardar persona'));
    } finally {
      setGuardandoPersona(false);
    }
  };

  // ===== Derivados =====
  const grupoSel  = v._detalle?.grupo || null;
  const sucSel    = v._detalle?.sucursal || null;
  const sucursalFaltante = !!v.cliente_grupo_id && !v.cliente_sucursal_id;

  // ===== RENDER =====
  return (
    <>
      {/* Toggle + boton crear cliente */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 10, flexWrap: 'wrap', marginBottom: 12,
      }}>
        <label style={{
          display: 'flex', alignItems: 'center', gap: 8,
          cursor: 'pointer', fontSize: 13, color: COLOR_GRIS,
        }}>
          <input
            type="checkbox"
            checked={sinCliente}
            onChange={(e) => toggleSinCliente(e.target.checked)}
          />
          <span>Crear sin cliente vinculado</span>
        </label>
        <button
          type="button"
          onClick={() => { setSinCliente(false); setModalNuevo(true); }}
          style={btnLinkLima}
        >+ Crear cliente nuevo</button>
      </div>

      {sinCliente && (
        <div style={cajaInfo}>
          Este expediente quedara sin cliente vinculado. Podras vincularlo despues
          desde la ficha del expediente.
        </div>
      )}

      {!sinCliente && (
        <div style={{
          background: '#fff', border: `1px solid ${COLOR_BORDE}`, borderRadius: 12,
          padding: 14, fontFamily: FF, display: 'flex', flexDirection: 'column', gap: 14,
        }}>
          {/* SELECTOR DE TIPO: Grupo | Empresa | Persona fisica */}
          <SegmentedTipo tipo={tipo} onCambio={cambiarTipo} esCelular={esCelular} />

          {/* ===== TIPO GRUPO: flujo grupo -> sucursal (+ contacto opcional) ===== */}
          {tipo === 'grupo' && (
            <>
              {/* GRUPO */}
              <Campo etiqueta="Grupo (cliente)" requerido>
                <Combo
                  placeholder={cargandoGrupos ? 'Cargando grupos...' : 'Seleccionar grupo cliente...'}
                  valorTexto={grupoSel?.nombre}
                  opciones={grupos}
                  renderItem={(g) => g.nombre}
                  filtra={(g, q) => g.nombre.toLowerCase().includes(q.toLowerCase())}
                  esIgual={(g) => Number(g.id) === Number(v.cliente_grupo_id)}
                  onSeleccionar={elegirGrupo}
                  vacioMensaje="No hay grupos. Crea un cliente nuevo."
                  footer={(
                    <button
                      type="button"
                      onClick={() => setModalNuevo(true)}
                      style={btnFooter}
                    >+ Crear cliente nuevo</button>
                  )}
                />
              </Campo>

              {/* SUCURSAL — la razon social se infiere automaticamente */}
              <Campo etiqueta="Sucursal" requerido alerta={sucursalFaltante}>
                <Combo
                  deshabilitado={!v.cliente_grupo_id}
                  placeholderDeshabilitado="Primero elige un grupo"
                  placeholder={cargandoSucursales ? 'Cargando sucursales...' : 'Seleccionar sucursal...'}
                  valorTexto={sucSel?.nombre_interno}
                  opciones={sucursales}
                  renderItem={(s) => s.nombre_interno}
                  filtra={(s, q) => s.nombre_interno.toLowerCase().includes(q.toLowerCase())}
                  esIgual={(s) => Number(s.id) === Number(v.cliente_sucursal_id)}
                  onSeleccionar={elegirSucursal}
                  vacioMensaje="Este grupo no tiene sucursales."
                  borderError={sucursalFaltante}
                />
                {sucursalFaltante && (
                  <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>
                    La sucursal es obligatoria si vinculas un grupo.
                  </div>
                )}
              </Campo>

              {/* PERSONA opcional (contacto) */}
              {v.cliente_sucursal_id && (
                <div style={{ borderTop: `1px solid ${COLOR_BORDE}`, paddingTop: 12 }}>
                  {v._detalle?.persona ? (
                    <div style={{ fontSize: 13, color: COLOR_OSCURO }}>
                      <strong>Contacto:</strong> {v._detalle.persona.nombre_completo}
                      {v._detalle.persona.celular && (
                        <span style={{ color: COLOR_GRIS_CL, marginLeft: 6 }}>
                          - {v._detalle.persona.celular}
                        </span>
                      )}
                    </div>
                  ) : agregarPersonaForm ? (
                    <FormPersonaInline
                      valor={agregarPersonaForm}
                      onCambio={setAgregarPersonaForm}
                      onCancelar={() => setAgregarPersonaForm(null)}
                      onGuardar={guardarPersonaInline}
                      guardando={guardandoPersona}
                      esCelular={esCelular}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setAgregarPersonaForm({ nombre: '', celular: '', email: '' })}
                      style={btnLinkLima}
                    >+ Agregar persona de contacto (opcional)</button>
                  )}
                </div>
              )}
            </>
          )}

          {/* ===== TIPO EMPRESA: razon social directa ===== */}
          {tipo === 'empresa' && (
            <Campo etiqueta="Empresa (razon social)" requerido>
              <Combo
                placeholder={cargandoRazones ? 'Cargando empresas...' : 'Seleccionar empresa...'}
                valorTexto={v._detalle?.razon_social?.razon_social}
                opciones={razones}
                renderItem={(r) => r.grupo_nombre ? `${r.razon_social} — ${r.grupo_nombre}` : r.razon_social}
                filtra={(r, q) => r.razon_social.toLowerCase().includes(q.toLowerCase())}
                esIgual={(r) => Number(r.id) === Number(v.cliente_razon_social_id)}
                onSeleccionar={elegirRazon}
                vacioMensaje="No hay empresas. Crea un cliente nuevo."
                footer={(
                  <button
                    type="button"
                    onClick={() => setModalNuevo(true)}
                    style={btnFooter}
                  >+ Crear cliente nuevo</button>
                )}
              />
            </Campo>
          )}

          {/* ===== TIPO PERSONA FISICA: persona directa ===== */}
          {tipo === 'persona' && (
            <Campo etiqueta="Persona fisica" requerido>
              <Combo
                placeholder={cargandoPersonas ? 'Cargando personas...' : 'Seleccionar persona...'}
                valorTexto={v._detalle?.persona?.nombre_completo}
                opciones={personas}
                renderItem={(p) => p.celular ? `${p.nombre_completo} — ${p.celular}` : p.nombre_completo}
                filtra={(p, q) => p.nombre_completo.toLowerCase().includes(q.toLowerCase())}
                esIgual={(p) => Number(p.id) === Number(v.cliente_persona_id)}
                onSeleccionar={elegirPersona}
                vacioMensaje="No hay personas. Crea un cliente nuevo."
                footer={(
                  <button
                    type="button"
                    onClick={() => setModalNuevo(true)}
                    style={btnFooter}
                  >+ Crear cliente nuevo</button>
                )}
              />
            </Campo>
          )}
        </div>
      )}

      <ModalNuevoClienteExpediente
        abierto={modalNuevo}
        onCerrar={() => setModalNuevo(false)}
        onCreado={onCreado}
        esCelular={esCelular}
      />
    </>
  );
}

// ============================================
// COMBO — wrapper alrededor de <select> HTML nativo
// ============================================
// Implementacion final tras 3 intentos fallidos con dropdowns custom: el
// <select> nativo cierra solo al elegir, soporta teclado, navegacion por
// primera letra, y se ve perfecto en mobile (rueda nativa de iOS/Android).
// La interfaz de props es la misma que tenia el Combo custom para no romper
// a los consumidores. Las props `filtra` y `placeholderDeshabilitado` se
// preservan en la firma pero no se usan (el select nativo no tiene buscador
// interno; el placeholderDeshabilitado se mapea al placeholder estandar
// cuando deshabilitado=true). El `footer` se renderiza DEBAJO del select.
function Combo({
  deshabilitado, placeholder, placeholderDeshabilitado,
  valorTexto, opciones, renderItem, filtra, esIgual, onSeleccionar,
  vacioMensaje, footer, borderError,
}) {
  // eslint-disable-next-line no-unused-vars
  const _filtraIgnorado = filtra; // preservada en API; el <select> no la usa

  const indiceSeleccionado = opciones.findIndex(o => esIgual(o));

  const handleChange = (e) => {
    const indice = parseInt(e.target.value, 10);
    if (indice >= 0 && opciones[indice]) {
      onSeleccionar(opciones[indice]);
    }
  };

  const textoPlaceholder = deshabilitado
    ? (placeholderDeshabilitado || placeholder)
    : placeholder;

  return (
    <div>
      <div style={{ position: 'relative' }}>
        <select
          disabled={deshabilitado}
          value={indiceSeleccionado >= 0 ? indiceSeleccionado : ''}
          onChange={handleChange}
          style={{
            width: '100%',
            padding: '10px 36px 10px 12px',
            border: `1px solid ${borderError ? '#dc2626' : COLOR_BORDE}`,
            borderRadius: 8,
            background: deshabilitado ? '#f9fafb' : '#fff',
            // 16px: el <select> tambien dispara el auto-zoom de iOS con <16px, y
            // este es el campo Cliente (obligatorio) del modal (veto Don Porfirio).
            fontSize: 16, minHeight: 44,
            fontFamily: FF,
            cursor: deshabilitado ? 'not-allowed' : 'pointer',
            color: indiceSeleccionado >= 0 ? COLOR_OSCURO : COLOR_GRIS_CL,
            appearance: 'none',
            WebkitAppearance: 'none',
            MozAppearance: 'none',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        >
          <option value="" disabled>
            {opciones.length === 0
              ? (vacioMensaje || 'Sin opciones disponibles')
              : textoPlaceholder}
          </option>
          {opciones.map((item, i) => (
            <option key={item.id ?? i} value={i}>
              {renderItem(item)}
            </option>
          ))}
        </select>

        {/* Flecha custom para que se vea consistente cross-browser */}
        <span style={{
          position: 'absolute', right: 12, top: '50%',
          transform: 'translateY(-50%)',
          color: COLOR_GRIS_CL, fontSize: 10,
          pointerEvents: 'none',
        }}>
          ▼
        </span>
      </div>

      {/* Footer (boton "+ Crear cliente nuevo") queda DEBAJO del select */}
      {footer && (
        <div style={{ marginTop: 8 }}>
          {footer}
        </div>
      )}
    </div>
  );
}

// ============================================
// Subcomponentes
// ============================================
// EXP-CLIENTE-CUALQUIERA: control segmentado para elegir el tipo de vinculo.
function SegmentedTipo({ tipo, onCambio, esCelular }) {
  const opciones = [
    { id: 'grupo',   label: 'Grupo' },
    { id: 'empresa', label: 'Empresa' },
    { id: 'persona', label: 'Persona fisica' },
  ];
  return (
    <div style={{
      display: 'flex', gap: 4, background: '#f1f5f9', borderRadius: 10,
      padding: 4, flexWrap: esCelular ? 'wrap' : 'nowrap',
    }}>
      {opciones.map((o) => {
        const activo = tipo === o.id;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onCambio(o.id)}
            style={{
              flex: esCelular ? '1 1 40%' : 1,
              // minHeight 44: tab tactil-safe (veto Don Porfirio).
              padding: '10px 10px', minHeight: 44,
              border: 'none', borderRadius: 8,
              background: activo ? COLOR_AZUL : 'transparent',
              color: activo ? '#fff' : COLOR_GRIS,
              fontWeight: 700, fontSize: 14, fontFamily: FF,
              cursor: 'pointer', transition: 'background 0.15s',
            }}
          >{o.label}</button>
        );
      })}
    </div>
  );
}

function Campo({ etiqueta, requerido, alerta, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontFamily: FF }}>
      <span style={{
        fontSize: 12, fontWeight: 600,
        color: alerta ? '#dc2626' : COLOR_GRIS,
      }}>
        {etiqueta}{requerido && <span style={{ color: '#dc2626', marginLeft: 3 }}>*</span>}
      </span>
      {children}
    </label>
  );
}

function FormPersonaInline({ valor, onCambio, onCancelar, onGuardar, guardando, esCelular }) {
  return (
    <div style={{
      background: '#f9fafb', border: `1px solid ${COLOR_BORDE}`,
      borderRadius: 10, padding: 14, fontFamily: FF,
    }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 10 }}>
        Persona de contacto
      </div>
      <div style={{
        display: 'grid', gap: 10,
        gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr',
      }}>
        <input
          type="text" value={valor.nombre}
          onChange={(e) => onCambio({ ...valor, nombre: e.target.value })}
          placeholder="Nombre completo *"
          style={inputMini}
        />
        <input
          type="tel" value={valor.celular}
          onChange={(e) => onCambio({ ...valor, celular: e.target.value })}
          placeholder="Celular"
          style={inputMini}
        />
        <input
          type="email" value={valor.email}
          onChange={(e) => onCambio({ ...valor, email: e.target.value })}
          placeholder="Email"
          style={{ ...inputMini, gridColumn: esCelular ? 'auto' : 'span 2' }}
        />
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 12, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancelar} disabled={guardando} style={btnSecundario}>
          Cancelar
        </button>
        <button
          type="button" onClick={onGuardar}
          disabled={guardando || !valor.nombre.trim()}
          style={{
            ...btnPrimarioLima,
            opacity: (!valor.nombre.trim() || guardando) ? 0.5 : 1,
          }}
        >{guardando ? 'Guardando...' : 'Guardar persona'}</button>
      </div>
    </div>
  );
}

// ============================================
// Estilos
// ============================================
const inputMini = {
  width: '100%', padding: '9px 10px',
  border: `1px solid ${COLOR_BORDE}`, borderRadius: 6,
  // 16px: evita el auto-zoom de iOS al enfocar (frente 3 captura movil).
  fontSize: 16, fontFamily: FF, color: COLOR_OSCURO,
  outline: 'none', boxSizing: 'border-box', background: '#fff',
};

const cajaInfo = {
  background: '#eef4fc', border: `1px solid #c7daf2`, color: COLOR_AZUL,
  padding: 14, borderRadius: 10, fontSize: 13, fontFamily: FF,
};

const btnLinkLima = {
  background: 'transparent', border: 'none', color: COLOR_LIMA,
  fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: FF,
  // MOVIL-GUARDAR: medido en 17 px de alto (imposible de atinar con el dedo).
  // Se le da area tactil de 44 sin moverlo de sitio ni cambiar su aspecto.
  padding: '0 4px', minHeight: 44, display: 'inline-flex', alignItems: 'center',
};

const btnFooter = {
  width: '100%', padding: '8px 14px',
  background: COLOR_LIMA, color: '#fff', border: 'none',
  borderRadius: 8, fontSize: 13, fontWeight: 700, fontFamily: FF, cursor: 'pointer',
  minHeight: 44, // tactil (medido en 33 px)
};

const btnPrimarioLima = {
  background: COLOR_LIMA, color: '#fff', border: 'none',
  borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 700,
  fontFamily: FF, cursor: 'pointer', minHeight: 36,
};

const btnSecundario = {
  background: '#fff', color: COLOR_GRIS, border: `1px solid ${COLOR_BORDE}`,
  borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600,
  fontFamily: FF, cursor: 'pointer', minHeight: 36,
};
